import React, { useState, useEffect } from 'react';
import { downloadApplicationDocument } from '../lib/fileUtils';
import { useData } from '../contexts/DataContext';
import { Application, ApplicationDocument } from '../types';
import { X, Save, FileText, Trash2, AlertCircle, Plus, Package, Info, Calculator, Paperclip, FolderOpen, Download, CheckCircle } from 'lucide-react';

interface ApplicationFormProps {
  application?: Application | null;
  onClose: () => void;
  preselectedTenderId?: string;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ application, onClose, preselectedTenderId }) => {
  const {
    tenders, 
    tenderStatuses,
    applicationStatuses, 
    organizations,
    products,
    currencyRates,
    applications,
    addApplication, 
    updateApplication
  } = useData();

  const [formData, setFormData] = useState({
    tender_id: application?.tender_id || preselectedTenderId || '',
    status_id: application?.status_id || '',
    submission_date: application?.submission_date ? new Date(application.submission_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    amount: application?.amount?.toString() || '',
    currency: application?.currency || 'CLP',
    proposal: application?.proposal || '',
    notes: application?.notes || '',
    margin: '0'
  });

  const [documents, setDocuments] = useState<ApplicationDocument[]>(application?.documents_metadata || []);
  const [selectedProducts, setSelectedProducts] = useState<Array<{
    id: string;
    product_id: string;
    quantity: number;
    months: number;
    custom_unit_value?: number;
  }>>(application?.cost_items || []);
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeDocumentTab, setActiveDocumentTab] = useState<'administrativos' | 'tecnicos' | 'financieros' | 'otros'>('administrativos');
  const [statusChangeBlocked, setStatusChangeBlocked] = useState(false);

  // Filtrar licitaciones disponibles para nueva postulación
  const getAvailableTenders = () => {
    if (application) {
      // Si estamos editando, solo mostrar la licitación actual
      return tenders.filter(tender => tender.id === application.tender_id);
    }

    // Para nueva postulación, filtrar licitaciones que:
    // 1. Están en estado "Publicada"
    // 2. No tienen postulación existente de esta empresa
    return tenders.filter(tender => {
      // 1. Solo mostrar licitaciones que están en estado de licitación "Publicada"
      const tenderStatus = tenderStatuses.find(s => s.id === tender.status_id);
      const isPublished = tenderStatus?.name === 'Publicada';
      
      // 2. Verificar que no existe ya una postulación para esta licitación
      const hasExistingApplication = applications.some(app => app.tender_id === tender.id);
      
      return isPublished && !hasExistingApplication;
    });
  };

  const availableTenders = getAvailableTenders();
  const currentTender = application 
    ? tenders.find(t => t.id === application.tender_id) 
    : availableTenders.find(t => t.id === formData.tender_id);

  // Obtener el estado "En Revision" por defecto (nuevo flujo)
  const getReviewApplicationStatus = () => {
    return applicationStatuses.find(status =>
      status.name === 'En Revision' ||
      status.name === 'En Análisis' ||
      status.order_index === 2
    ) || applicationStatuses.find(status =>
      status.order_index === 1
    ) || applicationStatuses[0];
  };

  // Obtener el estado "Ofertada"
  const getOfferedApplicationStatus = () => {
    return applicationStatuses.find(status =>
      status.name === 'Ofertada' ||
      status.order_index === 3
    );
  };

  // Obtener el estado "Descartada"
  useEffect(() => {
    if (!application && applicationStatuses.length > 0) {
      const reviewStatus = getReviewApplicationStatus();
      if (reviewStatus) {
        setFormData(prev => ({ ...prev, status_id: reviewStatus.id }));
      }
    }
  }, [application, applicationStatuses]);

  // Auto-set status to "En Revision" when tender is selected (only for new applications)
  useEffect(() => {
    if (!application && formData.tender_id && applicationStatuses.length > 0) {
      const reviewStatus = getReviewApplicationStatus();
      if (reviewStatus) {
        setFormData(prev => ({ ...prev, status_id: reviewStatus.id }));
      }
    }
  }, [formData.tender_id, application, applicationStatuses]);

  // Verificar si hay documentos sin firmar cuando se intenta cambiar a estado "Ofertada"
  useEffect(() => {
    if (formData.status_id) {
      const selectedStatus = applicationStatuses.find(s => s.id === formData.status_id);
      const offeredStatus = getOfferedApplicationStatus();
      
      if (selectedStatus?.name === 'Ofertada' || selectedStatus?.id === offeredStatus?.id) {
        // Verificar si hay documentos sin firmar
        const hasUnsignedRequiredDocs = documents.some(doc => doc.required && !doc.signed);
        setStatusChangeBlocked(hasUnsignedRequiredDocs);
      } else {
        setStatusChangeBlocked(false);
      }
    }
  }, [formData.status_id, documents, applicationStatuses]);

  // Función para convertir monedas a CLP
  const convertToCLP = (amount: number, currency: string): number => {
    if (currency === 'CLP') return amount;
    
    const latestRate = currencyRates
      .filter(r => r.currency === currency)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    
    return latestRate ? amount * latestRate.rate : amount;
  };

  // Calcular total automáticamente cuando cambian los productos
  useEffect(() => {
    const total = selectedProducts.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      if (!product) return sum;
      
      // Use custom unit value if provided, otherwise use product's unit value
      const unitValue = item.custom_unit_value !== undefined ? item.custom_unit_value : product.unit_value;
      const unitValueCLP = convertToCLP(unitValue, product.currency);
      return sum + (unitValueCLP * item.quantity * item.months);
    }, 0);

    // Aplicar margen si existe
    const margin = parseFloat(formData.margin) || 0;
    const totalWithMargin = total * (1 + margin / 100);

    setFormData(prev => ({ ...prev, amount: Math.round(totalWithMargin).toString() }));
  }, [selectedProducts, products, currencyRates, formData.margin]);

  // Funciones para manejar productos
  const addProduct = () => {
    // Get contract months from the selected tender
    const defaultMonths = currentTender?.contract_months || 1;
    
    const newProduct = {
      id: Date.now().toString(),
      product_id: '',
      quantity: 1,
      months: defaultMonths
    };
    setSelectedProducts(prev => [...prev, newProduct]);
  };

  const removeProduct = (index: number) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const updateProduct = (index: number, field: string, value: any) => {
    setSelectedProducts(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Funciones para manejar documentos
  const addDocument = (type: 'administrativos' | 'tecnicos' | 'financieros' | 'otros') => {
    const newDocument: ApplicationDocument = {
      id: Date.now().toString(),
      title: '',
      type,
      file_name: '',
      file_size: 0,
      required: false,
      signed: false,
      uploaded_at: new Date().toISOString()
    };
    setDocuments(prev => [...prev, newDocument]);
  };

  const updateDocument = (index: number, field: keyof ApplicationDocument, value: any) => {
    setDocuments(prev => prev.map((doc, i) => 
      i === index ? { ...doc, [field]: value } : doc
    ));
    
    // If updating file, also update the title to the file name without extension
    if (field === 'file_name' && typeof value === 'string') {
      const fileName = value.split('.')[0]; // Remove extension
      setDocuments(prev => prev.map((doc, i) => 
        i === index ? { ...doc, title: fileName } : doc
      ));
    }
  };

  const handleFileUpload = (index: number, file: File) => {
    setDocuments(prev => prev.map((doc, i) => 
      i === index ? { 
        ...doc, 
        file_name: file.name,
        file_size: file.size,
        title: file.name.split('.')[0] // Set title to filename without extension
      } : doc
    ));
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const markAsSigned = (index: number) => {
    setDocuments(prev => prev.map((doc, i) => 
      i === index ? { ...doc, signed: true } : doc
    ));
  };

  const getDocumentsByType = (type: 'administrativos' | 'tecnicos' | 'financieros' | 'otros') => {
    return documents.filter(doc => doc.type === type);
  };

  const getDocumentTypeIcon = (type: 'administrativos' | 'tecnicos' | 'financieros' | 'otros') => {
    switch (type) {
      case 'administrativos':
        return <FileText className="h-4 w-4" />;
      case 'tecnicos':
        return <Package className="h-4 w-4" />;
      case 'financieros':
        return <Calculator className="h-4 w-4" />;
      case 'otros':
        return <FolderOpen className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getDocumentTypeColor = (type: 'administrativos' | 'tecnicos' | 'financieros' | 'otros') => {
    switch (type) {
      case 'administrativos':
        return 'text-blue-600 bg-blue-100';
      case 'tecnicos':
        return 'text-green-600 bg-green-100';
      case 'financieros':
        return 'text-purple-600 bg-purple-100';
      case 'otros':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const documentTabs = [
    { id: 'administrativos', label: 'Administrativos', icon: FileText },
    { id: 'tecnicos', label: 'Técnicos', icon: Package },
    { id: 'financieros', label: 'Financieros', icon: Calculator },
    { id: 'otros', label: 'Otros', icon: FolderOpen }
  ] as const;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.tender_id) newErrors.tender_id = 'La licitación es requerida';
    if (!formData.submission_date) newErrors.submission_date = 'La fecha de envío es requerida';
    
    // Verificar si hay documentos requeridos sin firmar cuando se intenta cambiar a "Ofertada"
    const selectedStatus = applicationStatuses.find(s => s.id === formData.status_id);
    const offeredStatus = getOfferedApplicationStatus();
    
    if ((selectedStatus?.name === 'Ofertada' || selectedStatus?.id === offeredStatus?.id) && 
        documents.some(doc => doc.required && !doc.signed)) {
      newErrors.status_id = 'No se puede cambiar a estado Ofertada sin firmar todos los documentos requeridos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const applicationData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        submission_date: new Date(formData.submission_date).toISOString(),
        documents: documents.map(doc => doc.file_name), // Mantener compatibilidad con el campo documents
        documents_metadata: documents, // Nuevo campo con metadatos completos
        cost_items: selectedProducts
      };

      if (application) {
        updateApplication(application.id, applicationData);
      } else {
        addApplication(applicationData);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving application:', error);
    }
  };

  const getTotalCLP = () => {
    return selectedProducts.reduce((total, item) => {
      const product = products.find(p => p.id === item.product_id);
      if (!product) return total;
      
      // Use custom unit value if provided, otherwise use product's unit value
      const unitValue = item.custom_unit_value !== undefined ? item.custom_unit_value : product.unit_value;
      const unitValueCLP = convertToCLP(unitValue, product.currency);
      return total + (unitValueCLP * item.quantity * item.months);
    }, 0);
  };

  const selectedTender = availableTenders.find(t => t.id === formData.tender_id);
  
  // For editing mode, get the tender from all tenders (not just available ones)
  const currentOrganization = currentTender ? organizations.find(org => org.id === currentTender.organization_id) : null;

  // Download document function
  const handleDownloadDocument = async (doc: ApplicationDocument) => {
    try {
      await downloadApplicationDocument(doc);
    } catch (error) {
      console.error('Error downloading document:', error);
      // Error message is already shown in downloadApplicationDocument
    }
  };

  // Get application status name
  const getApplicationStatusName = (id: string) => {
    return applicationStatuses.find(status => status.id === id)?.name || 'No especificado';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  {application ? 'Editar Postulación' : 'Nueva Postulación'}
                </h2>
                {formData.status_id && (
                  <span 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${applicationStatuses.find(s => s.id === formData.status_id)?.color}20`,
                      color: applicationStatuses.find(s => s.id === formData.status_id)?.color
                    }}
                  >
                    {getApplicationStatusName(formData.status_id)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {application ? 'Modifica los datos de la postulación' : 'Crea una nueva postulación a licitación'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Licitación *
              </label>
              {!!application || preselectedTenderId ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700">
                  {currentTender ? `${currentTender.tender_code} - ${currentTender.title}` : 'Licitación no encontrada'}
                </div>
              ) : (
                <select
                  name="tender_id"
                  value={formData.tender_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecciona una licitación</option>
                  {availableTenders.map(tender => (
                    <option key={tender.id} value={tender.id}>
                      {tender.tender_code} - {tender.title}
                    </option>
                  ))}
                </select>
              )}
              {errors.tender_id && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.tender_id}
                </p>
              )}
              {!!application && (
                <p className="text-xs text-gray-500 mt-1">
                  La licitación asociada no se puede modificar
                </p>
              )}
              {!application && availableTenders.length === 0 && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">No hay licitaciones disponibles</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Solo se muestran licitaciones que están en estado "Publicada" y que no tienen una postulación existente.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!application && availableTenders.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  Se muestran solo las licitaciones disponibles para postular (sin postulación existente)
                </p>
              )}
            </div>

            {currentTender && (
              <div className="lg:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Información de la Licitación</h4>
                    <p className="text-sm text-blue-700 mt-1">{currentTender.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <span className="font-medium text-blue-900">Monto:</span>
                        <span className="text-blue-700 ml-2">
                          {new Intl.NumberFormat('es-CL').format(currentTender.amount)} {currentTender.currency}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-blue-900">Cierre:</span>
                        <span className="text-blue-700 ml-2">
                          {new Date(currentTender.closing_date).toLocaleDateString('es-CL')}
                        </span>
                      </div>

                      {currentTender.has_technical_visit && (
                        <div>
                          <span className="font-medium text-blue-900">Visita Técnica:</span>
                          <span className="text-blue-700 ml-2">
                            {currentTender.technical_visit_date ? 
                              new Date(currentTender.technical_visit_date).toLocaleDateString('es-CL') : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentTender.has_gso && (
                        <div>
                          <span className="font-medium text-blue-900">GSO:</span>
                          <span className="text-blue-700 ml-2">
                            {currentTender.gso_amount ? 
                              `${new Intl.NumberFormat('es-CL').format(currentTender.gso_amount)} ${currentTender.currency}` : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentTender.has_gfc && (
                        <div>
                          <span className="font-medium text-blue-900">GFC:</span>
                          <span className="text-blue-700 ml-2">
                            {currentTender.gfc_amount ? 
                              `${new Intl.NumberFormat('es-CL').format(currentTender.gfc_amount)} ${currentTender.currency}` : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentOrganization && (
                        <div className="col-span-2">
                          <span className="font-medium text-blue-900">Organización:</span>
                          <span className="text-blue-700 ml-2">{currentOrganization.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show tender info for editing mode even if not in available tenders */}
            {application && currentTender && !selectedTender && (
              <div className="lg:col-span-2 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 w-5 text-gray-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Información de la Licitación</h4>
                    <p className="text-sm text-gray-700 mt-1">{currentTender.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <span className="font-medium text-gray-900">Monto:</span>
                        <span className="text-gray-700 ml-2">
                          {new Intl.NumberFormat('es-CL').format(currentTender.amount)} {currentTender.currency}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Cierre:</span>
                        <span className="text-gray-700 ml-2">
                          {new Date(currentTender.closing_date).toLocaleDateString('es-CL')}
                        </span>
                      </div>
                      {currentTender.fecha_preguntas && (
                        <div>
                          <span className="font-medium text-gray-900">Preguntas:</span>
                          <span className="text-gray-700 ml-2">
                            {new Date(currentTender.fecha_preguntas).toLocaleDateString('es-CL')}
                          </span>
                        </div>
                      )}
                      {currentTender.has_technical_visit && (
                        <div>
                          <span className="font-medium text-gray-900">Visita Técnica:</span>
                          <span className="text-gray-700 ml-2">
                            {currentTender.technical_visit_date ? 
                              new Date(currentTender.technical_visit_date).toLocaleDateString('es-CL') : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentTender.has_gso && (
                        <div>
                          <span className="font-medium text-gray-900">GSO:</span>
                          <span className="text-gray-700 ml-2">
                            {currentTender.gso_amount ? 
                              `${new Intl.NumberFormat('es-CL').format(currentTender.gso_amount)} ${currentTender.currency}` : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentTender.has_gfc && (
                        <div>
                          <span className="font-medium text-gray-900">GFC:</span>
                          <span className="text-gray-700 ml-2">
                            {currentTender.gfc_amount ? 
                              `${new Intl.NumberFormat('es-CL').format(currentTender.gfc_amount)} ${currentTender.currency}` : 
                              'Sí'}
                          </span>
                        </div>
                      )}
                      {currentOrganization && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-900">Organización:</span>
                          <span className="text-gray-700 ml-2">
                            {currentOrganization.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Organization information for editing mode */}
            {application && currentOrganization && (
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organización
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700">
                  {currentOrganization.name}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Organización que publicó la licitación
                </p>
              </div>
            )}

            {!application && (
              <input
                type="hidden"
                name="status_id"
                value={formData.status_id}
              />
            )}

            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Envío *
                </label>
                <input
                  type="date"
                  name="submission_date"
                  value={formData.submission_date}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.submission_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.submission_date && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.submission_date}
                  </p>
                )}
              </div>


            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              {application ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                  {getApplicationStatusName(formData.status_id)}
                </div>
              ) : (
                <select
                  name="status_id"
                  value={formData.status_id}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.status_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Seleccionar estado</option>
                  {applicationStatuses
                    .filter(status => ['En Revision', 'Ofertada', 'Descartada'].includes(status.name))
                    .sort((a, b) => a.order_index - b.order_index)
                    .map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {application ? 'El estado no se puede modificar en postulaciones existentes' : 'Flujo: En Revision → Ofertada → Descartada'}
              </p>
              {errors.status_id && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.status_id}
                </p>
              )}
              {statusChangeBlocked && (
                <p className="mt-1 text-sm text-amber-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Debe firmar todos los documentos requeridos antes de cambiar a Ofertada
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monto Ofertado (Presupuesto: {currentTender ? `${currentTender.currency} ${Math.round(currentTender.amount).toLocaleString()}` : 'no disponible'})
              </label>
                              <input
                  type="text"
                  name="amount"
                  value={new Intl.NumberFormat('es-CL').format(Math.round(parseFloat(formData.amount) || 0))}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  placeholder="0"
                />
              <p className="text-xs text-gray-500 mt-1">
                El monto se calcula automáticamente basado en los productos seleccionados
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Moneda
              </label>
              {application ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                  {formData.currency === 'CLP' ? 'CLP - Peso Chileno' : 
                   formData.currency === 'USD' ? 'USD - Dólar Americano' : 
                   formData.currency === 'EUR' ? 'EUR - Euro' : formData.currency}
                </div>
              ) : (
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="CLP">CLP - Peso Chileno</option>
                  <option value="USD">USD - Dólar Americano</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {application ? 'La moneda no se puede modificar en postulaciones existentes' : 'Selecciona la moneda de tu propuesta'}
              </p>
            </div>

            {/* Sección de Productos */}
            <div className="lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Productos y Servicios
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona los productos/servicios que incluirás en tu propuesta
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addProduct}
                  className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-700 transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar Producto
                </button>
              </div>

              {/* Grilla de Productos */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Header de la grilla */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                    <div className="col-span-4">Producto</div>
                    <div className="col-span-2">Valor Unitario</div>
                    <div className="col-span-2">Cantidad</div>
                    <div className="col-span-2">Períodos</div>
                    <div className="col-span-2">Total</div>
                  </div>
                </div>

                {/* Filas de productos */}
                <div className="divide-y divide-gray-200">
                  {selectedProducts.map((item, index) => {
                    const product = products.find(p => p.id === item.product_id);
                    const originalUnitValue = product ? product.unit_value : 0;
                    const unitValue = item.custom_unit_value !== undefined ? item.custom_unit_value : originalUnitValue;
                    const unitValueCLP = convertToCLP(unitValue, product?.currency || 'CLP');
                    const totalItemCLP = unitValueCLP * item.quantity * item.months;

                    return (
                      <div key={item.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          {/* Producto */}
                          <div className="col-span-4">
                            <select
                              value={item.product_id}
                              onChange={(e) => updateProduct(index, 'product_id', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Seleccionar producto</option>
                              {products.map(product => (
                                <option key={product.id} value={product.id}>
                                  {product.code} - {product.description}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Valor Unitario */}
                          <div className="col-span-2">
                            <div className="flex flex-col">
                              <input
                                type="number"
                                value={item.custom_unit_value !== undefined ? item.custom_unit_value : (product?.unit_value || 0)}
                                onChange={(e) => updateProduct(index, 'custom_unit_value', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                              {product && (
                                <span className="text-xs text-gray-500 mt-1">
                                  {product.currency} {product.unit_value.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Cantidad */}
                          <div className="col-span-2">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                              min="1"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Períodos */}
                          <div className="col-span-2">
                            <input
                              type="number"
                              value={item.months}
                              onChange={(e) => updateProduct(index, 'months', parseInt(e.target.value) || 1)}
                              min="1"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Total */}
                          <div className="col-span-2 flex items-center justify-between">
                            <span className="text-sm font-medium text-green-600">
                              CLP {Math.round(totalItemCLP).toLocaleString()}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeProduct(index)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                              title="Eliminar producto"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Mensaje cuando no hay productos */}
                {selectedProducts.length === 0 && (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No hay productos seleccionados</p>
                    <p className="text-xs">Agrega productos para calcular el monto automáticamente</p>
                  </div>
                )}
              </div>

              {/* Total general */}
              {selectedProducts.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="text-lg font-medium text-blue-900">Total Propuesta:</span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">
                      CLP {Math.round(getTotalCLP()).toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Campo de Margen */}
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-blue-900 mr-3">Margen (%):</span>
                      <input
                        type="number"
                        name="margin"
                        value={formData.margin}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                      />
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-green-600">
                        Monto Final: CLP {Math.round(parseFloat(formData.amount) || 0).toLocaleString()}
                      </div>
                      {parseFloat(formData.margin) > 0 && (
                        <div className="text-xs text-gray-500">
                          +{formData.margin}% de margen aplicado
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-blue-600 mt-2">
                    * Los valores se convierten automáticamente a CLP usando las tasas de cambio más recientes
                  </p>
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Propuesta
              </label>
              <textarea
                name="proposal"
                value={formData.proposal}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.proposal ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe tu propuesta técnica y comercial..."
              />
              {errors.proposal && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.proposal}
                </p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas Adicionales
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Notas internas, observaciones, etc..."
              />
            </div>

          </div>

          {/* Sección de Documentos */}
          <div className="lg:col-span-2">
            <div className="mb-4 mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documentos de la Postulación
              </label>
              <p className="text-xs text-gray-500 mb-1">
                Organiza tus documentos por categorías para una mejor gestión
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Importante: Documentos Firmados</p>
                    <p className="text-xs text-amber-700">
                      Los documentos marcados como "requeridos" deben ser firmados antes de cambiar el estado a "Ofertada".
                      Descargue los documentos, fírmelos y luego súbalos nuevamente marcándolos como firmados.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs para categorías de documentos */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex space-x-8">
                {documentTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeDocumentTab === tab.id;
                  const documentsCount = getDocumentsByType(tab.id).length;
                  
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveDocumentTab(tab.id)}
                      className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        isActive
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {tab.label}
                      {documentsCount > 0 && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                          isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {documentsCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Contenido de la pestaña activa */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-medium text-gray-900">
                  Documentos {documentTabs.find(t => t.id === activeDocumentTab)?.label}
                </h4>
                <button
                  type="button"
                  onClick={() => addDocument(activeDocumentTab)}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar Documento
                </button>
              </div>

              <div className="space-y-3">
                {getDocumentsByType(activeDocumentTab).map((doc) => {
                  const globalIndex = documents.findIndex(d => d.id === doc.id);
                  return (
                    <div key={doc.id} className={`border rounded-lg p-4 ${doc.signed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Título del documento */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Título del Documento *
                          </label>
                          <input
                            type="text"
                            value={doc.title}
                            onChange={(e) => updateDocument(globalIndex, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ej: Certificado de experiencia"
                          />
                        </div>

                        {/* Archivo */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Archivo *
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleFileUpload(globalIndex, file);
                                }
                              }}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                          </div>
                          {doc.file_name && (
                            <div className="mt-2 flex items-center text-xs text-gray-600">
                              <Paperclip className="h-3 w-3 mr-1" />
                              <span>{doc.file_name}</span>
                              {doc.file_size && (
                                <span className="ml-2 text-gray-400">
                                  ({(doc.file_size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              )}
                              {doc.signed && (
                                <span className="ml-2 text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex items-center">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Firmado
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Opciones adicionales */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`required-${doc.id}`}
                              checked={doc.required}
                              onChange={(e) => updateDocument(globalIndex, 'required', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`required-${doc.id}`} className="ml-2 text-sm text-gray-700">
                              Documento requerido
                            </label>
                          </div>
                          
                          {doc.file_name && (
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadDocument(doc)}
                                className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Descargar para firma
                              </button>
                              
                              {!doc.signed && (
                                <button
                                  type="button"
                                  onClick={() => markAsSigned(globalIndex)}
                                  className="text-green-600 hover:text-green-800 text-xs flex items-center"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Marcar como firmado
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(doc.type)}`}>
                            {getDocumentTypeIcon(doc.type)}
                            <span className="ml-1">{documentTabs.find(t => t.id === doc.type)?.label}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeDocument(globalIndex)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {getDocumentsByType(activeDocumentTab).length === 0 && (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    {getDocumentTypeIcon(activeDocumentTab)}
                    <p className="text-sm mt-2">No hay documentos {documentTabs.find(t => t.id === activeDocumentTab)?.label.toLowerCase()}</p>
                    <p className="text-xs">Haz clic en "Agregar Documento" para subir archivos</p>
                  </div>
                )}
              </div>

              {/* Resumen de documentos */}
              {documents.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Resumen de Documentos</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    {documentTabs.map(tab => {
                      const count = getDocumentsByType(tab.id).length;
                      const Icon = tab.icon;
                      return (
                        <div key={tab.id} className="flex items-center">
                          <Icon className="h-3 w-3 mr-1 text-blue-600" />
                          <span className="text-blue-700">{tab.label}: {count}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                    <p className="text-xs text-blue-600">
                      Total: {documents.length} documento{documents.length !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center space-x-4">
                      <p className="text-xs text-blue-600">
                        Requeridos: {documents.filter(d => d.required).length}
                      </p>
                      <p className="text-xs text-blue-600">
                        Firmados: {documents.filter(d => d.signed).length}/{documents.filter(d => d.required).length} requeridos
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={statusChangeBlocked && formData.status_id === getOfferedApplicationStatus()?.id}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{application ? 'Actualizar' : 'Crear'} Postulación</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationForm;